import React, {memo, useEffect, useState} from 'react';
import {useTheme} from "../../../common/ProjectThemeProvider";
import {useToast} from "../../../common/fusion-ui/provider/ToastProvider";
import {
    checkNullArr,
    checkNullStr,
    getArrLen,
    getDefStr,
    getJsonValue,
    getJsonValueFromNestedKeys,
    isBoolTrue,
    isJsonValueTrue,
    limitStringWords,
    setTabTitle,
    sortJsonListAdvance
} from "../../../common/fusion-ui/utils/ReactUtils";
import {formatDateValue, printError, printLog} from "../../../common/CommonUtils";
import {AdminModules, AdminReqUrls} from "../../config/AdminConfig";
import {AdminInterface} from "../../components";
import {sendAdminRequest} from "../../utils/AdminUtils";
import {Constants, CssVariant, DefStrNA, IconClass} from "../../../common/fusion-ui/config/FusionConfig";
import {Checkbox, CircleLoading, Flexbox, GridBox, Image, Label, LoadingSection, NullMsgLabel, Section} from "../../../common/fusion-ui";
import {AnimationType, FramerAnimator} from "../../../common/fusion-ui/animation/FramerAnimator";
import {ThemedButton, ThemedIconButton} from "../../../common/components";
import * as XLSX from 'xlsx';

const BackupDbPage = memo(() => {
    const TAG = "BackupDbPage";

    const moduleName = AdminModules.BackupDb;
    const animator = new FramerAnimator();

    const theme = useTheme();
    const {showToast, changeToastVariant} = useToast();

    const Fields = {
        submitBtn_AsLoading: 'submitBtn_AsLoading',
        submitBtn_disabled: 'submitBtn_disabled',
        formatColumns: 'formatColumns',

        currCompanyId: 'currCompanyId',
        currCompanyName: 'currCompanyName',
        currCompanyTablesData: 'currCompanyTablesData',
        currCompanyDataLoading: 'currCompanyDataLoading',
        currCompanyDataFound: 'currCompanyDataFound',
        currCompanyDataLoadingMsg: 'currCompanyDataLoadingMsg',
        currCompanyTablesData_SortedAsc: 'currCompanyTablesData_SortedAsc',

        companies: 'companies',
        companiesLoading: 'companiesLoading',
        companiesLoadingMsg: 'companiesLoadingMsg',
        companiesLoadingSuccess: 'companiesLoadingSuccess',
        companiesDataFetched: 'companiesDataFetched',
    };
    const [fieldsData, setFieldsData] = useState({});

    useEffect(() => {
        setTabTitle(moduleName);
        fetchCompanies();
    }, []);

    return (<>
        <AdminInterface moduleName={moduleName} tag={TAG}>
            <div className={``}>
                {<LoadingSection
                    labelClassName={'m-2 w-100 fs-normal'}
                    heightClass={75}
                    color={theme.text70}
                    success={isFieldTrue(Fields.companiesLoadingSuccess)}
                    loading={isFieldTrue(Fields.companiesLoading)}
                    msg={getField(Fields.companiesLoadingMsg)}
                />}

                {(!isFieldTrue(Fields.companiesLoading) && isFieldTrue(Fields.companiesLoadingSuccess)) && <div>
                    {animator.setAnimationType(AnimationType.SLIDE_UP).setDelay(100).renderChildren(<div>
                        {/*page-description*/}
                        <Label className={'text-theme-resp fs-md w-100 px-1x'} textAt={'start'}>
                            {`Download database tables based on the chosen company or client. Select specific tables for backup, providing flexibility and control over the data export process. Streamlines data management and backups for storage, analysis, or archiving purposes.`}
                        </Label>

                        <GridBox className={'mt-2 px-1'} md={[3, 9]}>
                            {/*companies*/}
                            <Section
                                className={'px-0 py-2 h-100'}
                                borderless={true}
                                shadowVariant={'xs'}
                                title={'Companies'}
                                titleClassName={'fw-semi-bold px-3 fs-normal select-none text-theme-resp-60'}
                                titleAt={'start'}>
                                <div className={'px-3 mt-2x scroller scroller-y-auto scroll-dark'}
                                     style={{maxHeight: '55vh'}}>
                                    {checkNullArr(getField(Fields.companies)) ? getField(Fields.companies).map((item, index) => {
                                        return <div key={index + item.name}>
                                            <div
                                                className={`mb-1x d-flex w-100 jc-start al-center flex-nowrap fd-row cursor-pointer highlight-theme-hover transition-sm rounded-3 px-2 py-1 ${getField(Fields.currCompanyId) == item.id ? 'highlight-theme' : ''}`}
                                                onClick={() => fetchCurrCompanyTablesData(item.id, item.text)}>
                                                {/*img*/}
                                                <Image
                                                    wrapperClassName={''}
                                                    className={'rounded-3 border-none'}
                                                    src={getDefStr(item.image, 'https://icons.iconarchive.com/icons/martz90/circle-addon1/256/facebook-pages-icon.png')}
                                                    width={'20px'}
                                                    height={'auto'}
                                                    objectFit={'fill'}
                                                />

                                                {/*name*/}
                                                <div className={'ps-2'}>
                                                    <Label className={'text-theme-resp fs-sm-md text-nowrap cursor-pointer'}
                                                           tooltip={(item.text + '').length >= 20 ? item.text : ''}>
                                                        {limitStringWords(item.text, 20, '', '...')}
                                                    </Label>
                                                </div>
                                            </div>
                                        </div>
                                    }) : <NullMsgLabel className={'fw-semi-bold'} color={theme.text50} textAt={'start'}>
                                        {getField(Fields.companiesLoadingMsg, `No companies available!`)}
                                    </NullMsgLabel>}
                                </div>
                            </Section>

                            {/*tables*/}
                            <Section
                                className={'px-0 py-2 h-100'}
                                borderless={true}
                                shadowVariant={'xs'}
                                title={<div className={'w-100 d-flex flex-wrap fd-row jc-between jc-start-sm al-center al-center-sm'}>
                                    <span className={'text-theme-resp-60'}>
                                            {`Available tables `}{isFieldTrue(Fields.currCompanyDataFound) ? <span className={'text-theme-resp'}>
                                                {` for ${getField(Fields.currCompanyName)} (${getField(Fields.currCompanyId)})`}</span> : ''}
                                    </span>

                                    <Flexbox className={'my-1'} alignAt={'center'}
                                             disabled={isFieldTrue(Fields.currCompanyDataLoading) || !isFieldTrue(Fields.currCompanyDataFound)}
                                             wrap={''} direction={''}
                                             justifyAtSm={'start'}
                                    >
                                        <Checkbox
                                            wrapperClassName={'mx-1 fs-md select-none fw-normal'}
                                            variant={''}
                                            fontSize={''}
                                            color={theme.text}
                                            py={0}
                                            defaultChecked={isFieldTrue(Fields.formatColumns)}
                                            checked={isFieldTrue(Fields.formatColumns)}
                                            onChange={(e, value) => {
                                                // log('cb-all.onChange.value:', value);
                                                updateField(Fields.formatColumns, value);
                                            }}>Format Columns</Checkbox>

                                        {/*refresh*/}
                                        <ThemedIconButton
                                            className={'p-2 fs-md text-center-force'}
                                            tooltip={'Refresh'}
                                            mx={1}
                                            outlined={true}
                                            size={'24px'}
                                            icon={IconClass.refresh}
                                            borderWidth={0}
                                            asLoading={isFieldTrue(Fields.currCompanyDataLoading)}
                                            disabled={isFieldTrue(Fields.currCompanyDataLoading)}
                                            onClick={() => {
                                                fetchCurrCompanyTablesData(getField(Fields.currCompanyId), getField(Fields.currCompanyName), false);
                                            }}
                                        />

                                        {/*dl-btn*/}
                                        <ThemedButton
                                            className={'w-auto px-2 fs-sm py-1 text-nowrap'}
                                            text={isFieldTrue(Fields.submitBtn_AsLoading) ? 'Downloading.....' : 'Download data'}
                                            asLoading={isFieldTrue(Fields.submitBtn_AsLoading)}
                                            disabled={isFieldTrue(Fields.submitBtn_disabled)}
                                            ml={1}
                                            onClick={handleDownloadCurrCompanyData}
                                        />
                                    </Flexbox>
                                </div>}
                                titleColor={theme.text80}
                                titleClassName={'fw-semi-bold px-3 fs-normal'}
                                titleAt={'start'}>
                                <div className={'px-3 mt-2x h-100'}>
                                    {/*loader*/}
                                    {isFieldTrue(Fields.currCompanyDataLoading) && <CircleLoading wrapperHeight={'auto'}/>}

                                    {/*avail-tables list*/}
                                    {isFieldTrue(Fields.currCompanyDataFound) ? <div>
                                        <div className={'mt-2'}>
                                            <Section
                                                className={'p-0'}
                                                borderColor={theme.text20}
                                                roundedVariant={3}>
                                                <div className={'w-100 position-relative h-100 scroller scroller-auto scroll-dark'}
                                                     style={{maxHeight: '50vh'}}>
                                                    <table className={'w-100 border-none'}
                                                           style={{color: theme.text}}>
                                                        <thead className={'w-100 position-sticky'}
                                                               style={{top: 0, backgroundColor: theme.bg, zIndex: 5}}>
                                                        <tr>
                                                            <th className={'px-2 d-flexx w-auto'}>
                                                                <Checkbox
                                                                    wrapperClassName={'w-auto'}
                                                                    className={''}
                                                                    variant={''}
                                                                    color={theme.text}
                                                                    highlightHoverVariant={''}
                                                                    py={0}
                                                                    px={0}
                                                                    defaultChecked={getField(Fields.currCompanyTablesData).every(item => isBoolTrue(item.selected))}
                                                                    checked={getField(Fields.currCompanyTablesData).every(item => isBoolTrue(item.selected))}
                                                                    onChange={(e, value) => {
                                                                        // log('cb-all.onChange.value:', value);
                                                                        try {
                                                                            let updatedList = getField(Fields.currCompanyTablesData);
                                                                            updatedList = checkNullArr(updatedList) ? updatedList : [];
                                                                            updatedList = updatedList.map((mapItem) => {
                                                                                return {
                                                                                    ...mapItem,
                                                                                    selected: value
                                                                                };
                                                                            });
                                                                            updateField(Fields.currCompanyTablesData, updatedList);
                                                                        } catch (error) {
                                                                            toast('Operation failed, please try again!');
                                                                            logErr(error);
                                                                        }
                                                                    }}>
                                                                    {`${getArrLen(getField(Fields.currCompanyTablesData).filter(t => isBoolTrue(t.selected)))}/${getArrLen(getField(Fields.currCompanyTablesData))}`}
                                                                </Checkbox>
                                                            </th>
                                                            <th className={'px-2'}>No.</th>
                                                            <th className={'px-2'}>
                                                                <span className={'cursor-pointer rounded-1 transition-sm highlight-dark-hover px-1'}
                                                                      onClick={() => {
                                                                          try {
                                                                              let ascSorted = !isFieldTrue(Fields.currCompanyTablesData_SortedAsc);
                                                                              let updatedList = getField(Fields.currCompanyTablesData);
                                                                              updatedList = sortJsonListAdvance(updatedList, 'name', ascSorted);
                                                                              updateField(Fields.currCompanyTablesData, updatedList);
                                                                              updateField(Fields.currCompanyTablesData_SortedAsc, ascSorted);
                                                                          } catch (error) {
                                                                              toast('Operation failed, please try again!');
                                                                              logErr(error);
                                                                          }
                                                                      }}>Table Name</span>
                                                            </th>
                                                            <th className={'px-2'}>
                                                                <span
                                                                    className={'cursor-pointer rounded-1 transition-sm highlight-dark-hover px-1'}
                                                                    onClick={() => {
                                                                        try {
                                                                            let ascSorted = !isFieldTrue(Fields.currCompanyTablesData_SortedAsc);
                                                                            let updatedList = getField(Fields.currCompanyTablesData);
                                                                            updatedList = sortJsonListAdvance(updatedList, 'recordCount', ascSorted);
                                                                            updateField(Fields.currCompanyTablesData, updatedList);
                                                                            updateField(Fields.currCompanyTablesData_SortedAsc, ascSorted);
                                                                        } catch (error) {
                                                                            toast('Operation failed, please try again!');
                                                                            logErr(error);
                                                                        }
                                                                    }}>Record Count</span>
                                                            </th>
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        {getField(Fields.currCompanyTablesData).map((item, index) => {
                                                            return <tr key={index + item.name}
                                                                       style={{borderTop: `.5px solid ${theme.text10}`}}
                                                                       className={`transition-sm text-theme-resp highlight-dark-hover`}>

                                                                {/*selected-cb*/}
                                                                <td className={'px-2 text-center'}>
                                                                    <Checkbox
                                                                        wrapperClassName={'w-auto'}
                                                                        className={'fw-bold'}
                                                                        variant={''}
                                                                        color={theme.text}
                                                                        highlightHoverVariant={''}
                                                                        py={0}
                                                                        defaultChecked={isBoolTrue(item.selected)}
                                                                        checked={isBoolTrue(item.selected)}
                                                                        onChange={(e, value) => {
                                                                            // log('cb-item.onChange.value:', value, 'item', item, 'item.selected:', item.selected);
                                                                            try {
                                                                                let updatedList = getField(Fields.currCompanyTablesData);
                                                                                updatedList = updatedList.map((mapItem) => {
                                                                                    if (mapItem.id == item.id) {
                                                                                        return {
                                                                                            ...mapItem,
                                                                                            selected: !isBoolTrue(item.selected)
                                                                                        };
                                                                                    }
                                                                                    return mapItem;
                                                                                });
                                                                                updateField(Fields.currCompanyTablesData, updatedList);
                                                                            } catch (error) {
                                                                                toast('Operation failed, please try again!');
                                                                                logErr(error);
                                                                            }
                                                                        }}/>
                                                                </td>

                                                                {/*no.*/}
                                                                <td className={'px-2'}>{index + 1}</td>

                                                                {/*name*/}
                                                                <td className={'px-2'}><span className={'px-1'}>{item.name}</span></td>

                                                                {/*record-count*/}
                                                                <td className={'px-2'}><span className={'px-1'}>{getDefStr(item.recordCount, DefStrNA)}</span>
                                                                </td>
                                                            </tr>
                                                        })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </Section>
                                        </div>
                                    </div> : !isFieldTrue(Fields.currCompanyDataLoading) &&
                                        <NullMsgLabel className={'fw-semi-bold'} color={theme.text50} textAt={'start'}>
                                            {!checkNullStr(getField(Fields.currCompanyId)) ? 'Please select a company!' : getField(Fields.currCompanyDataLoadingMsg, `No data available!`)}
                                        </NullMsgLabel>}
                                </div>
                            </Section>
                        </GridBox>
                    </div>)}
                </div>}
            </div>
        </AdminInterface>
    </>)

    async function fetchCompanies(checkBefore = true) {
        const fun = 'fetchCompanies:';
        if (!checkBefore || (checkBefore && !isFieldTrue(Fields.companiesDataFetched))) {
            updateField(Fields.companiesDataFetched, true);
            updateField(Fields.companiesLoading, true);
            updateField(Fields.companiesLoadingSuccess, false);
            updateField(Fields.companiesLoadingMsg, null);
            updateField(Fields.companies, []);
            updateField(Fields.currCompanyId, null);
            updateField(Fields.currCompanyName, null);
            updateField(Fields.currCompanyTablesData, {});
            updateField(Fields.currCompanyDataLoading, false);
            updateField(Fields.currCompanyDataFound, false);
            updateField(Fields.currCompanyDataLoadingMsg, null);
            try {
                //send-request
                const REQUEST_OPTIONS = {fetchImages: true};
                sendAdminRequest({
                    reqUrl: AdminReqUrls.admin__moduleAlloc__mainPage_fetch_companies,
                    reqOptions: REQUEST_OPTIONS,
                    onFetched: ((fetchedData) => {
                        log(fun, 'fetchedData:', fetchedData);
                        const comps = getJsonValueFromNestedKeys(fetchedData, ['data', 'companies'], []);
                        const success = checkNullArr(comps);
                        if (success) {
                            updateField(Fields.companies, comps);
                        } else {
                            toast("No companies found!", CssVariant.danger);
                            updateField(Fields.companiesLoadingMsg, "No companies found!");
                        }
                        updateField(Fields.companiesLoading, false);
                        updateField(Fields.companiesLoadingSuccess, success);
                    }),
                    onError: ((error) => {
                        toast("Couldn't load companies, please try again!", CssVariant.danger);
                        updateField(Fields.companiesLoadingMsg, "Couldn't load companies, please try again!");
                        updateField(Fields.companiesLoading, false);
                        updateField(Fields.companiesLoadingSuccess, false);
                        logErr(fun, error);
                    }),
                });
            } catch (error) {
                toast("Couldn't load companies, please try again!", CssVariant.danger);
                updateField(Fields.companiesLoadingMsg, "Couldn't load companies, please try again!");
                updateField(Fields.companiesLoading, false);
                updateField(Fields.companiesLoadingSuccess, false);
                logErr(fun, error);
            }
        }
    }

    async function fetchCurrCompanyTablesData(id, name, checkBefore = true) {
        const fun = 'fetchCurrCompanyTablesData:';

        //Return early if data already fetched
        if (checkBefore && id == getField(Fields.currCompanyId)) {
            return false;
        }

        try {
            updateField(Fields.currCompanyId, id);
            updateField(Fields.currCompanyName, name);
            updateField(Fields.currCompanyTablesData, {});
            updateField(Fields.currCompanyDataLoading, true);
            updateField(Fields.currCompanyDataFound, false);
            updateField(Fields.currCompanyDataLoadingMsg, null);

            //send-request
            const REQUEST_OPTIONS = {companyId: id};
            sendAdminRequest({
                reqUrl: AdminReqUrls.admin__backup_db__mainPage_fetch_curr_company_data,
                reqOptions: REQUEST_OPTIONS,
                onFetched: ((fetchedData) => {
                    log(fun, 'fetchedData:', fetchedData);
                    const tablesData = getJsonValue(fetchedData, 'data', []);
                    const success = isJsonValueTrue(fetchedData, 'success');
                    const message = getJsonValue(fetchedData, 'message');
                    if (success) {
                        toast(message, CssVariant.success);
                        updateField(Fields.currCompanyTablesData, tablesData);
                    } else {
                        toast(message, CssVariant.danger);
                        updateField(Fields.currCompanyDataLoadingMsg, message);
                    }
                    updateField(Fields.currCompanyDataLoading, false);
                    updateField(Fields.currCompanyDataFound, success);
                }),
                onError: ((error) => {
                    toast("Couldn't load data, please try again!", CssVariant.danger);
                    updateField(Fields.currCompanyDataLoadingMsg, "Couldn't load data, please try again!");
                    updateField(Fields.currCompanyDataLoading, false);
                    updateField(Fields.currCompanyDataFound, false);
                    logErr(fun, error);
                    updateField(Fields.currCompanyId, null);
                    updateField(Fields.currCompanyName, null);
                }),
            });
        } catch (error) {
            toast("Couldn't load data, please try again!", CssVariant.danger);
            updateField(Fields.currCompanyDataLoadingMsg, "Couldn't load data, please try again!");
            updateField(Fields.currCompanyDataLoading, false);
            updateField(Fields.currCompanyDataFound, false);
            logErr(fun, error);
            updateField(Fields.currCompanyId, null);
            updateField(Fields.currCompanyName, null);
        }
    }

    async function handleDownloadCurrCompanyData() {
        const fun = 'handleDownloadCurrCompanyData:';
        try {
            //Check if some tables are selected
            const tablesData = getField(Fields.currCompanyTablesData);
            if (checkNullArr(tablesData) && !checkNullArr(tablesData.filter(t => isJsonValueTrue(t, 'selected')))) {
                toast("Please select some tables for backup!", CssVariant.danger);
                return false;
            }

            updateField(Fields.submitBtn_AsLoading, true);
            updateField(Fields.submitBtn_disabled, true);

            //send-request
            const REQUEST_OPTIONS = {
                companyId: getField(Fields.currCompanyId),
                formatColumns: isFieldTrue(Fields.formatColumns),
                tablesData: getField(Fields.currCompanyTablesData)
            };
            sendAdminRequest({
                reqUrl: AdminReqUrls.admin__backup_db__mainPage_downloadCurrCompanyTablesData,
                reqOptions: REQUEST_OPTIONS,
                onFetched: ((fetchedData) => {
                    log(fun, 'fetchedData:', fetchedData);

                    const tablesData = getJsonValue(fetchedData, 'data', []);
                    const success = isJsonValueTrue(fetchedData, 'success');
                    const message = getJsonValue(fetchedData, 'message');
                    if (success) {
                        toast(message, CssVariant.success);

                        //Download excel fle
                        // tablesData-FORMAT = {
                        //           table1: [
                        //                  { id: 1, company_id: 100, name: 'Record 1' },
                        //                  { id: 2, company_id: 100, name: 'Record 2' }
                        //               ],
                        //  ...rest
                        // };

                        const workbook = XLSX.utils.book_new();

                        // Iterate over each table (sheet) in the tablesData
                        for (const [tableName, tableData] of Object.entries(tablesData)) {
                            // Convert table data to worksheet format (array of objects -> worksheet)
                            const worksheet = XLSX.utils.json_to_sheet(tableData);

                            // Append the worksheet to the workbook with the table name as the sheet name
                            XLSX.utils.book_append_sheet(workbook, worksheet, tableName);
                        }
                        const excelName = `${getField(Fields.currCompanyName)}_${getField(Fields.currCompanyId)}__Tables_Data__${formatDateValue(new Date(), Constants.DD_MMM_YYYY_DASH)}.xlsx`;
                        log(fun, 'excelName:', excelName);

                        // Write the workbook to an Excel file
                        XLSX.writeFile(workbook, excelName);

                        toast(`Excel file created successfully: '${excelName}'`, CssVariant.info);
                    } else {
                        toast(message, CssVariant.danger);
                    }
                    updateField(Fields.submitBtn_AsLoading, false);
                    updateField(Fields.submitBtn_disabled, false);
                }),
                onError: ((error) => {
                    toast("Couldn't save changes, please try again!", CssVariant.danger);
                    updateField(Fields.currCompanyDataLoadingMsg, "Couldn't save changes, please try again!");
                    updateField(Fields.submitBtn_AsLoading, false);
                    updateField(Fields.submitBtn_disabled, false);
                    logErr(fun, error);
                }),
            });
        } catch (error) {
            toast("Couldn't save changes, please try again!", CssVariant.danger);
            updateField(Fields.currCompanyDataLoadingMsg, "Couldn't save changes, please try again!");
            updateField(Fields.submitBtn_AsLoading, false);
            updateField(Fields.submitBtn_disabled, false);
            logErr(fun, error);
        }
    }


    function updateField(field, value) {
        setFieldsData(prevState => ({...prevState, [field]: value}));
    }

    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue);
    }

    function isFieldTrue(field) {
        return isBoolTrue(getField(field), true, true);
    }

    function toast(text, variant) {
        showToast(text + '');
        changeToastVariant(variant);
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }
});
export default BackupDbPage;
